import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import { formatSavedAgoLabel } from "../utils/formatting";
import { CharCounter } from "../components/CharCounter";
import { clampText, textLimit } from "../utils/textLimits";

type Props = {
  approveLabel?: string;
  disabled?: boolean;
  submitting?: boolean;
  onApprove: () => void;
  onReject: (reason: string) => void | Promise<void>;
  onSendBack?: (reason: string) => void | Promise<void>;
  onSave?: () => void | Promise<void>;
  saving?: boolean;
  lastSavedAt?: Date | null;
  approvedNotice?: string | null;
  rejectPromptTitle?: string;
  sendBackPromptTitle?: string;
  sendBackPromptPlaceholder?: string;
  errorMessage?: string | null;
  reasonMaxLength?: number;
};

type DialogMode = "reject" | "sendBack" | null;

export function ReviewerActionsFooter({
  approveLabel = "Approve",
  disabled,
  submitting,
  onApprove,
  onReject,
  onSendBack,
  onSave,
  saving,
  lastSavedAt,
  approvedNotice,
  rejectPromptTitle = "Reject application",
  sendBackPromptTitle = "Send back to vendor",
  sendBackPromptPlaceholder = "Reason to send back (vendor will see this)",
  errorMessage,
  reasonMaxLength,
}: Props) {
  const [dialog, setDialog] = useState<DialogMode>(null);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [dialogSubmitting, setDialogSubmitting] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const reasonMax = reasonMaxLength ?? textLimit("REMARKS");

  useEffect(() => {
    if (!lastSavedAt) return;
    const id = setInterval(() => setNowTick(Date.now()), 15_000);
    return () => clearInterval(id);
  }, [lastSavedAt]);

  const closeDialog = () => {
    setDialog(null);
    setReason("");
    setReasonError(null);
    setDialogSubmitting(false);
  };

  const confirm = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setReasonError(
        dialog === "sendBack"
          ? "Reason is required to send back"
          : "Reason is required to reject",
      );
      return;
    }
    setReasonError(null);
    setDialogSubmitting(true);
    try {
      if (dialog === "sendBack") {
        await onSendBack?.(trimmed);
      } else {
        await onReject(trimmed);
      }
      closeDialog();
    } catch {
      // Keep the dialog open so the typed reason isn't lost. Parent typically
      // also surfaces its own errorMessage above the button row for wider
      // context; this inline message covers the reason-preservation case.
      setReasonError("Couldn't submit. Please try again.");
      setDialogSubmitting(false);
    }
  };

  const dialogTitle =
    dialog === "sendBack" ? sendBackPromptTitle : rejectPromptTitle;
  const dialogConfirmLabel =
    dialog === "sendBack" ? "Confirm send back" : "Confirm reject";

  return (
    <>
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      {approvedNotice && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {approvedNotice}
        </Alert>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mt: 2,
        }}
      >
        {onSave && (
          <>
            <Button
              type="button"
              variant="outlined"
              disabled={disabled || submitting || saving}
              onClick={() => {
                void onSave();
              }}
              sx={{
                borderColor: "#E0E4EF",
                color: "text.primary",
                "&:hover": { borderColor: "#C7CDDF", bgcolor: "#F5F7FC" },
              }}
            >
              {saving ? (
                <CircularProgress size={16} sx={{ color: "text.secondary" }} />
              ) : (
                "Save"
              )}
            </Button>
            {lastSavedAt && !saving && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontSize: 12 }}
              >
                {formatSavedAgoLabel(lastSavedAt, nowTick)}
              </Typography>
            )}
          </>
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          type="button"
          variant="outlined"
          color="error"
          disabled={disabled || submitting || saving}
          onClick={() => setDialog("reject")}
        >
          Reject
        </Button>
        {onSendBack && (
          <Button
            type="button"
            variant="outlined"
            disabled={disabled || submitting || saving}
            onClick={() => setDialog("sendBack")}
            sx={{
              borderColor: "#E0E4EF",
              color: "text.primary",
              "&:hover": { borderColor: "#C7CDDF", bgcolor: "#F5F7FC" },
            }}
          >
            Send Back
          </Button>
        )}
        <Button
          type="button"
          variant="contained"
          disabled={disabled || submitting || saving}
          onClick={onApprove}
          endIcon={!submitting ? <ArrowForwardIcon /> : undefined}
        >
          {submitting ? (
            <CircularProgress size={18} sx={{ color: "#FFF" }} />
          ) : (
            approveLabel
          )}
        </Button>
      </Box>

      <Dialog open={dialog !== null} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            inputProps={{ maxLength: reasonMax }}
            placeholder={
              dialog === "sendBack"
                ? sendBackPromptPlaceholder
                : "Reason for rejection"
            }
            value={reason}
            onChange={(e) => {
              setReason(clampText(e.target.value, reasonMax));
              if (reasonError) setReasonError(null);
            }}
            error={!!reasonError}
            helperText={reasonError}
            sx={{ mt: 1 }}
          />
          <CharCounter length={reason.length} max={reasonMax} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={dialogSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={dialog === "sendBack" ? "primary" : "error"}
            onClick={confirm}
            disabled={dialogSubmitting}
          >
            {dialogSubmitting ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : (
              dialogConfirmLabel
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
